// src/components/atomic-crm/root/DatabaseHealthCheck.tsx
import { useEffect, useState } from "react";
import { type DataProvider, useTranslate } from "ra-core";
import {
  checkDatabaseHealth,
  type DatabaseHealthStatus,
} from "@/lib/database-health-check";
import { getSupabaseConfig } from "@/lib/supabase-config";
import { DatabaseSetupGuide } from "../setup/DatabaseSetupGuide";
import { useMigrationContextSafe } from "@/contexts/MigrationContext";
import { supabase } from "../providers/supabase/supabase";

interface DatabaseHealthCheckProps {
  children: React.ReactNode;
  dataProvider: DataProvider;
}

const CACHE_KEY = "crm_db_health";
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

type CachedHealth = { status: DatabaseHealthStatus; cachedAt: number };

function readCache(): CachedHealth | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedHealth;
    if (Date.now() - parsed.cachedAt > CACHE_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeCache(status: DatabaseHealthStatus) {
  try {
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ status, cachedAt: Date.now() }),
    );
  } catch {
    /* ignore quota errors */
  }
}

/**
 * Detect network/offline failures — these are NOT schema problems.
 * We must not surface the setup guide for a flaky connection.
 */
function isNetworkError(error: unknown): boolean {
  if (!error) return false;
  const text = String((error as any)?.message ?? error).toLowerCase();
  return [
    "failed to fetch",
    "networkerror",
    "network error",
    "offline",
    "enotfound",
    "econnrefused",
    "dns",
    "timeout",
    "abort",
    "canceled",
    "cancelled",
  ].some((s) => text.includes(s));
}

export function DatabaseHealthCheck({
  children,
  dataProvider,
}: DatabaseHealthCheckProps) {
  const [healthStatus, setHealthStatus] = useState<DatabaseHealthStatus | null>(
    null,
  );
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const translate = useTranslate();
  const migrationContext = useMigrationContextSafe();

  // ---- 1. Determine if current user is an admin ---------------------------
  // Non-admin users should NEVER see the schema/setup guide.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: userData } = await supabase.auth.getUser();
        if (!userData?.user) {
          if (!cancelled) setIsAdmin(false);
          return;
        }
        const { data: sale } = await supabase
          .from("sales")
          .select("administrator")
          .eq("auth_user_id", userData.user.id)
          .maybeSingle();
        if (!cancelled) setIsAdmin(!!sale?.administrator);
      } catch {
        if (!cancelled) setIsAdmin(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // ---- 2. Background health check (non-blocking, network-aware) ----------
  useEffect(() => {
    let cancelled = false;

    // Seed from cache immediately so we don't re-show the guide
    const cached = readCache();
    if (cached) {
      setHealthStatus(cached.status);
      if (!cached.status.isHealthy && migrationContext) {
        migrationContext.setSuppressMigrationBanner(true);
      }
    }

    async function runCheck() {
      try {
        const status = await checkDatabaseHealth(dataProvider);
        if (cancelled) return;
        setHealthStatus(status);
        writeCache(status);
        migrationContext?.setSuppressMigrationBanner(!status.isHealthy);
      } catch (error) {
        if (isNetworkError(error)) {
          // 🔑 KEY FIX: a flaky connection is NOT a schema problem.
          // Leave any cached "healthy" state untouched.
          console.warn(
            "[DatabaseHealthCheck] Network unavailable — skipping update.",
          );
          return;
        }
        console.error("[DatabaseHealthCheck] Non-network error:", error);
        // For non-network errors, also do nothing — do not flip to "unhealthy".
      }
    }

    // Defer so first paint is instant; the app is already rendered below.
    const timer = setTimeout(runCheck, 200);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [dataProvider, migrationContext]);

  // ---- 3. Decide whether to show the setup guide -------------------------
  // Only admins with a POSITIVE "tables missing" report see the guide.
  const showSetupGuide =
    isAdmin === true &&
    healthStatus !== null &&
    healthStatus.isHealthy === false;

  if (showSetupGuide) {
    const config = getSupabaseConfig();
    if (config) {
      return (
        <DatabaseSetupGuide
          missingTables={healthStatus!.missingTables}
          supabaseUrl={config.url}
        />
      );
    }
  }

  // ✅ In every other case, render children immediately.
  //    No spinner. No blocking. Non-admins never see the guide.
  return <>{children}</>;
}
